/**
 * Version Rollback Modal
 * Provides version rollback functionality with confirmation and validation
 */

import React, { useState } from 'react';
import { DocumentVersion } from '../../../shared/document/types';
import { DocumentService } from '../../../shared/document/DocumentService';
import { 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface VersionRollbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  targetVersion: DocumentVersion;
  currentVersion: DocumentVersion;
  onRollbackComplete?: () => void;
}

export function VersionRollbackModal({
  isOpen,
  onClose,
  documentId,
  targetVersion,
  currentVersion,
  onRollbackComplete,
}: VersionRollbackModalProps): React.JSX.Element {
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleRollback = async () => {
    if (confirmationText !== 'ROLLBACK') {
      return;
    }

    setIsProcessing(true);
    try {
      // Create a new version with the target version's content
      const targetContent = await DocumentService.getLexicalContent(targetVersion.document_id);
      
      await DocumentService.saveLexicalContent(documentId, targetContent, {
        rollbackFrom: currentVersion.version,
        rollbackTo: targetVersion.version,
        rollbackReason: 'User-initiated rollback',
        rollbackTimestamp: new Date().toISOString(),
      });

      onRollbackComplete?.();
      onClose();
    } catch (error) {
      console.error('Rollback failed:', error);
      // Handle error - could show error message
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmRollback = () => {
    setShowConfirmation(true);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setConfirmationText('');
  };

  if (!isOpen) return <></>;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                  Rollback to Version {targetVersion.version}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    This will create a new version with the content from version {targetVersion.version}. 
                    The current version ({currentVersion.version}) will be preserved in the version history.
                  </p>
                </div>

                {/* Version Comparison */}
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                    <div className="flex items-center space-x-2">
                      <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Current Version {currentVersion.version}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(currentVersion.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex items-center justify-center">
                    <ArrowPathIcon className="h-4 w-4 text-gray-400" />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                    <div className="flex items-center space-x-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        Target Version {targetVersion.version}
                      </span>
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                      {new Date(targetVersion.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Confirmation */}
                {showConfirmation && (
                  <div className="mt-4">
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
                      <p className="text-sm text-red-800 dark:text-red-200 mb-2">
                        <strong>Warning:</strong> This action cannot be undone. Type "ROLLBACK" to confirm.
                      </p>
                      <input
                        type="text"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder="Type ROLLBACK to confirm"
                        className="w-full px-3 py-2 text-sm border border-red-300 dark:border-red-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {!showConfirmation ? (
              <>
                <button
                  type="button"
                  onClick={handleConfirmRollback}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Rollback
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleRollback}
                  disabled={confirmationText !== 'ROLLBACK' || isProcessing}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Rolling back...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Confirm Rollback
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VersionRollbackModal; 